'use client';

import { Fragment, type CSSProperties, type ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { SIcon } from '../SIcon';
import { STooltip } from '../STooltip';
import { SButton } from '../SButton';
import { SInput, type SInputProps } from '../SInput';
import { STextarea, type STextareaProps } from '../STextarea';
import { SNumberInput, type SNumberInputProps } from '../SNumberInput';
import { SSelect, type SSelectProps } from '../SSelect';
import { SRadioGroup, type SRadioGroupProps } from '../SRadio';
import { SCheckbox, type SCheckboxProps } from '../SCheckbox';
import { SDatePicker, type SDatePickerProps } from '../SDatePicker';
import { SDateRangePicker, type SDateRangePickerProps } from '../SDateRangePicker';
import { SFilePicker, type SFilePickerProps } from '../SFilePicker';

export type SKeyValueFieldType =
  | 'text'
  | 'input'
  | 'textarea'
  | 'number-input'
  | 'select'
  | 'radio'
  | 'checkbox'
  | 'date-picker'
  | 'date-range-picker'
  | 'file-picker';

/** field type과 무관한 공통 속성 (레이아웃·레이블·스팬 등) */
export interface SKeyValueFieldBase {
  name: string;
  label?: string;
  required?: boolean;
  helpText?: string[];
  hideTh?: boolean;
  thRowSpan?: number;
  thColSpan?: number;
  tdRowSpan?: number;
  tdColSpan?: number;
  thWidth?: number | string;
  thClass?: string;
  tdClass?: string;
  /** 셀 커스텀 렌더 (React 확장). 지정 시 type/options는 무시되고 이 노드가 그대로 렌더됩니다. */
  render?: ReactNode;
}

/**
 * `options`에서 제외되는 키.
 * - `onValueChange`: 테이블이 `onChange`로 승격시키므로 field 단위로 지정할 수 없습니다.
 * - `value`: 지정은 가능하나 `values` prop이 같은 name을 가지면 그쪽이 우선합니다. (타입은 유지)
 */
type FieldOptions<P> = Omit<P, 'onValueChange'>;

/**
 * `type`에 따라 `options`가 해당 컴포넌트의 props로 좁혀지는 discriminated union.
 * `type` 생략 시 `text`로 동작합니다.
 */
export type SKeyValueFieldByType =
  | { type?: 'text'; options?: { value?: ReactNode } }
  | { type: 'input'; options?: FieldOptions<SInputProps> }
  | { type: 'textarea'; options?: FieldOptions<STextareaProps> }
  | { type: 'number-input'; options?: FieldOptions<SNumberInputProps> }
  | { type: 'select'; options?: FieldOptions<SSelectProps> }
  | { type: 'radio'; options?: FieldOptions<SRadioGroupProps> }
  | { type: 'checkbox'; options?: FieldOptions<SCheckboxProps> }
  | { type: 'date-picker'; options?: FieldOptions<SDatePickerProps> }
  | { type: 'date-range-picker'; options?: FieldOptions<SDateRangePickerProps> }
  | { type: 'file-picker'; options?: FieldOptions<SFilePickerProps> };

export type SKeyValueField = SKeyValueFieldBase & SKeyValueFieldByType;

/**
 * field type별 `onChange`로 올라오는 값 타입.
 * - `string`: input · textarea · select · radio · date-picker
 * - `number`: number-input
 * - `boolean`: checkbox
 * - `string[]`: date-range-picker
 */
export type SKeyValueFieldValue = string | number | boolean | string[];

/**
 * `onChange` 콜백 detail.
 * 제네릭 `V`에 폼 값 형태(`{ [name]: value }`)를 넘기면 `name`/`value`/`values`가 정밀하게 타이핑됩니다.
 * 미지정 시 `value`는 {@link SKeyValueFieldValue} 유니온으로 잡힙니다.
 */
export interface SKeyValueChangeDetail<
  V extends Record<string, SKeyValueFieldValue> = Record<string, SKeyValueFieldValue>,
> {
  /** 변경된 field의 name */
  name: Extract<keyof V, string>;
  /** 변경된 field의 새 값 */
  value: V[Extract<keyof V, string>];
  /** 변경이 반영된 전체 값 객체 (`{ [name]: value }`). controlled 패턴에서 그대로 상태에 반영하면 됩니다. */
  values: V;
}

export interface SKeyValueTableProps {
  /** 2차원 배열. row → [th|td] 필드들 */
  fields?: SKeyValueField[][];
  /**
   * field name을 key로 하는 값 객체 (`{ [name]: value }`).
   * 지정 시 해당 field의 값으로 사용되며, field별 `options.value`보다 우선합니다.
   * `onChange`의 `detail.values`와 함께 controlled 패턴으로 사용합니다.
   */
  values?: Record<string, unknown>;
  /** 우측 검색 패널 */
  search?: boolean;
  /** border-radius 제어 */
  radius?: 'default' | 'useTop' | 'full';
  /** 값 변경 (sdChange) */
  onChange?: (detail: SKeyValueChangeDetail) => void;
  /** 검색 클릭 (sdSearch) */
  onSearch?: () => void;
  className?: string;
  style?: CSSProperties;
}

const cssLen = (v?: number | string) =>
  v == null ? undefined : typeof v === 'number' ? `${v}px` : v;

/** SKeyValueTable — sd-key-value-table 포팅. th/td 폼 레이아웃 + 필드 렌더러. */
export function SKeyValueTable({
  fields = [],
  values = {},
  search = false,
  radius = 'default',
  onChange,
  onSearch,
  className,
  style,
}: SKeyValueTableProps) {
  if (fields.length === 0) return null;

  const emit = (name: string, value: unknown) =>
    onChange?.({
      name,
      value: value as SKeyValueFieldValue,
      values: { ...values, [name]: value } as Record<string, SKeyValueFieldValue>,
    });

  const cellHeight = 'var(--cmp-table-keyValue-height)';
  const cellPadY = 'var(--cmp-table-keyValue-body-paddingY)';

  const renderField = (f: SKeyValueField): ReactNode => {
    if (f.render) return f.render;
    // values prop이 해당 field를 가지면 그 값을, 없으면 f.options.value를 사용.
    // 공개 타입(SKeyValueFieldByType)은 type별로 좁혀지지만, 여기서는 하나의 렌더러가 모든
    // type을 분기하므로 내부적으로만 느슨한 레코드로 다룬다.
    const opt: Record<string, any> = { ...(f.options ?? {}) };
    if (Object.prototype.hasOwnProperty.call(values, f.name)) opt.value = values[f.name];
    const up = (v: unknown) => emit(f.name, v);
    switch (f.type) {
      case 'input':
        return <SInput width="100%" {...opt} onValueChange={up} />;
      case 'textarea':
        return <STextarea width="100%" {...opt} onValueChange={up} />;
      case 'number-input':
        return <SNumberInput width="100%" {...opt} onValueChange={up} />;
      case 'select':
        return <SSelect width="100%" {...opt} onValueChange={up} />;
      case 'radio':
        return <SRadioGroup direction="horizontal" {...opt} onValueChange={up} />;
      case 'checkbox':
        return <SCheckbox {...opt} onValueChange={up} />;
      case 'date-picker':
        return <SDatePicker width="100%" {...opt} onValueChange={up} />;
      case 'date-range-picker':
        return <SDateRangePicker width="100%" {...opt} onValueChange={up} />;
      case 'file-picker':
        return <SFilePicker width="100%" {...opt} onValueChange={up} />;
      case 'text':
      default:
        return (
          <span className="text-[12px] text-[color:var(--sys-color-fg-primary)]">
            {opt.value ?? ''}
          </span>
        );
    }
  };

  // thRowSpan에 의해 가려지는 위치 자동 계산
  const skip: boolean[][] = fields.map(row => row.map(() => false));
  fields.forEach((row, r) =>
    row.forEach((f, c) => {
      const span = f.thRowSpan && f.thRowSpan > 1 ? f.thRowSpan : 1;
      for (let dr = 1; dr < span; dr++) {
        const tr = skip[r + dr];
        if (tr && c < tr.length) tr[c] = true;
      }
    }),
  );

  // 모든 row를 훑어 전체 컬럼 수 계산 (th/td + colSpan + hideTh/thRowSpan 반영)
  let maxCols = 0;
  fields.forEach((row, r) => {
    let cols = 0;
    row.forEach((f, c) => {
      const thCols = f.hideTh
        ? 0
        : skip[r]?.[c]
          ? 1
          : f.thColSpan && f.thColSpan > 1
            ? f.thColSpan
            : 1;
      const tdCols = f.tdColSpan && f.tdColSpan > 1 ? f.tdColSpan : 1;
      cols += thCols + tdCols;
    });
    if (cols > maxCols) maxCols = cols;
  });

  const radiusClass =
    radius === 'full'
      ? 'rounded-none'
      : radius === 'useTop'
        ? 'rounded-b-[8px] rounded-t-none'
        : 'rounded-[8px]';
  // 외곽 프레임: 보더 + 라운드 + overflow. (원본 sd-key-value-table__wrapper / .sd-key-value-table)
  // search 유무에 따라 flex 래퍼(양쪽 포함) 또는 table 컨테이너에 적용한다.
  const frameClass = cn(
    'overflow-hidden border border-solid border-[color:var(--cmp-table-border-color)]',
    radiusClass,
  );

  // 원본은 border-collapse: separate + 셀은 border-top(행 구분선)만, 첫 행은 top 보더 제거.
  // 세로 구분선은 없고 th(회색)/td(frame) 배경 차이로 구분한다. 프레임이 외곽 보더/라운드를 담당.
  const table = (
    <table
      className="w-full border-separate border-spacing-0"
      style={{ tableLayout: 'fixed', ...style }}
    >
      <colgroup>
        {Array.from({ length: maxCols }, (_, i) => {
          if (i % 2 !== 0) return <col key={`col-${i}`} />;
          const fieldIdx = i / 2;
          const fieldThWidth = fields.reduce<number | string | undefined>((found, row) => {
            if (found != null) return found;
            return row[fieldIdx]?.thWidth;
          }, undefined);
          return <col key={`col-${i}`} style={{ width: cssLen(fieldThWidth) ?? '120px' }} />;
        })}
      </colgroup>
      <tbody>
        {fields.map((row, r) => {
          const cellBorder =
            r === 0 ? '' : 'border-t border-solid border-[color:var(--cmp-table-border-color)]';
          return (
            <tr key={r}>
              {row.map((f, c) => {
                const showTh = !f.hideTh && !skip[r][c];
                return (
                  <Fragment key={`${r}-${c}`}>
                    {showTh && (
                      <th
                        key={`th-${r}-${c}`}
                        scope="row"
                        rowSpan={f.thRowSpan && f.thRowSpan > 1 ? f.thRowSpan : undefined}
                        colSpan={f.thColSpan && f.thColSpan > 1 ? f.thColSpan : undefined}
                        className={cn(
                          'box-border bg-[var(--cmp-table-header-bg)] px-[var(--cmp-table-header-paddingX)] text-left align-middle',
                          cellBorder,
                          f.thClass,
                        )}
                        style={{
                          height: cellHeight,
                          paddingTop: cellPadY,
                          paddingBottom: cellPadY,
                        }}
                      >
                        {/* 원본 th-inner: gap=header.gap(4), space-between으로 help를 우측 배치 / label group gap=content.gap(8) */}
                        <div className="flex h-full w-full items-center justify-between gap-[var(--cmp-table-header-gap)]">
                          <div className="inline-flex items-center gap-[8px]">
                            <span className="text-[12px] font-bold leading-[20px] text-[color:var(--sys-color-fg-primary)]">
                              {f.label}
                            </span>
                            {f.required && (
                              <SIcon name="asterisk" size={12} color="var(--sys-color-fg-accent)" />
                            )}
                          </div>
                          {f.helpText && f.helpText.length > 0 && (
                            <STooltip
                              trigger="hover"
                              icon="helpOutline"
                              iconSize={16}
                              color="var(--sys-color-fg-success)"
                              message={f.helpText}
                            />
                          )}
                        </div>
                      </th>
                    )}
                    <td
                      key={`td-${r}-${c}`}
                      rowSpan={f.tdRowSpan && f.tdRowSpan > 1 ? f.tdRowSpan : undefined}
                      colSpan={f.tdColSpan && f.tdColSpan > 1 ? f.tdColSpan : undefined}
                      className={cn(
                        'box-border bg-[var(--sys-color-bg-frame)] align-middle',
                        // text 타입(및 미지정 기본값)은 읽기 전용 콘텐츠라 넓은 paddingX(wide),
                        // 폼 필드 셀은 default paddingX 유지
                        f.type == null || f.type === 'text'
                          ? 'px-[var(--cmp-table-keyValue-body-paddingX-wide)]'
                          : 'px-[var(--cmp-table-keyValue-body-paddingX-default)]',
                        cellBorder,
                        f.tdClass,
                      )}
                      style={{ height: cellHeight, paddingTop: cellPadY, paddingBottom: cellPadY }}
                    >
                      {renderField(f)}
                    </td>
                  </Fragment>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  if (!search) return <div className={cn(frameClass, className)}>{table}</div>;

  return (
    <div className={cn('flex items-stretch', frameClass, className)}>
      <div className="min-w-0 flex-1">{table}</div>
      <div
        className="flex flex-shrink-0 items-center justify-center border-l border-solid border-[color:var(--cmp-table-border-color)] px-[var(--cmp-table-keyValue-search-paddingX)]"
        style={{ background: 'var(--cmp-table-keyValue-search-bg)' }}
      >
        <SButton color="neutral" outline size="md" label="검색" onClick={() => onSearch?.()} />
      </div>
    </div>
  );
}
